import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { Lightbulb, CheckCircle2, Camera, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

const ServiceWorkflow = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Service Visit Workflow" 
        description="Standard procedure for every pool service visit"
      >
        <section className="space-y-8">
          {/* Overview */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Consistent Service = Happy Clients</h2>
            <p className="text-muted-foreground">
              Following a consistent workflow ensures nothing gets missed, documentation is complete, 
              and every pool gets the same high-quality service. This workflow applies to standard 
              full-service visits — adjust as needed for chem-only or special service calls.
            </p>
          </div>

          {/* The Golden Rule */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <p className="text-lg font-semibold text-primary text-center">
              If it's not logged in the App, it didn't happen.
            </p>
            <p className="text-sm text-muted-foreground text-center mt-2">
              Jobs not fully logged are not eligible for payment.
            </p>
          </div>

          {/* Workflow Steps */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Service Workflow</h2>
            
            {/* Arrival */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">1</span>
                <h3 className="text-lg font-semibold text-foreground">Arrival</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Start job in the App (begins timer)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Quick visual scan of equipment pad</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note any obvious issues (leaks, equipment off, debris)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Check actuator toggle positions (should all be ON)</li>
              </ul>
              <div className="bg-primary/10 rounded-lg p-3 mt-4 ml-11">
                <p className="text-sm text-muted-foreground">
                  <Lightbulb className="h-4 w-4 text-primary inline mr-1" />
                  If something looks wrong immediately, take a photo before touching anything.
                </p>
              </div>
            </div>

            {/* Test */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">2</span>
                <h3 className="text-lg font-semibold text-foreground">Test Water Chemistry</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Collect sample from elbow-depth, away from returns</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Test free chlorine and pH (every visit)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Test alkalinity (weekly or if pH is unstable)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Test salt if applicable</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Log all readings in App before adding chemicals</li>
                <li><Camera className="h-4 w-4 text-primary inline mr-2" />Photo of test strip with readings visible</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 ml-11">
                Test first while equipment is running — this gives you time to determine what 
                chemicals to add while you clean.
              </p>
            </div>

            {/* Clean */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">3</span>
                <h3 className="text-lg font-semibold text-foreground">Clean</h3>
              </div>
              <ol className="space-y-3 text-muted-foreground ml-11">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">a.</span>
                  <span><strong className="text-foreground">Skim surface</strong> — Remove all floating debris</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">b.</span>
                  <span><strong className="text-foreground">Brush walls and steps</strong> — Push debris toward floor/drain</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">c.</span>
                  <span><strong className="text-foreground">Brush tile line</strong> — Prevent calcium buildup</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">d.</span>
                  <span><strong className="text-foreground">Vacuum floor</strong> — If needed (visible debris on bottom)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">e.</span>
                  <span><strong className="text-foreground">Empty baskets</strong> — Skimmer basket(s) and pump basket</span>
                </li>
              </ol>
              <div className="grid md:grid-cols-2 gap-3 mt-4 ml-11">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Before photo: pool condition</span>
                </div>
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">After photo: clean pool</span>
                </div>
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Skimmer basket (before/after)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Pump basket (before/after)</span>
                </div>
              </div>
            </div>

            {/* Treat */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">4</span>
                <h3 className="text-lg font-semibold text-foreground">Add Chemicals</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Calculate dosages based on test results</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Add chemicals (broadcast around pool or via skimmer)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Log all additions in App with exact quantities</li>
              </ul>
              <div className="bg-amber-500/10 rounded-lg p-3 mt-4 ml-11">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Remember:</strong> Don't add acid and chlorine at the 
                  same time. Wait at least 4 hours between them.
                </p>
              </div>
            </div>

            {/* Inspect */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">5</span>
                <h3 className="text-lg font-semibold text-foreground">Inspect Equipment</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Check filter pressure (note if cleaning needed)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Inspect pump for leaks, air bubbles, unusual noise</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Check salt cell display (if applicable)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Verify automation/timer settings look correct</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note any equipment issues with photos</li>
              </ul>
            </div>

            {/* Document */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">6</span>
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  Document & Complete
                </h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Verify all required photos uploaded</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />All readings logged</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />All chemical additions recorded</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Any issues or notes added</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Complete job checklist in App</li>
              </ul>
            </div>

            {/* Departure */}
            <div className="bg-card border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-semibold">7</span>
                <h3 className="text-lg font-semibold text-foreground">Departure Checklist</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground ml-11">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />All baskets back in place</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pump lid secured properly</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Equipment running normally</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Actuator toggles verified ON (pool/spa systems)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Gates closed/secured</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pool is safe and ready for use</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Mark job complete in App</li>
              </ul>
            </div>
          </div>

          {/* Photo Requirements */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Camera className="h-6 w-6 text-primary" />
              Photo Requirements
            </h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Every standard service visit requires these photos:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Required</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Pool before (overall condition)</li>
                    <li>• Pool after (clean)</li>
                    <li>• Skimmer basket(s)</li>
                    <li>• Pump basket</li>
                    <li>• Test strip with readings</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">As Needed</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Equipment issues</li>
                    <li>• Algae or water problems</li>
                    <li>• Damage or safety hazards</li>
                    <li>• Filter during cleaning (if applicable)</li>
                    <li>• Salt cell (if cleaned)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Time Management */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Time Management</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Average time targets for full-service visits:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong className="text-foreground">Standard pool:</strong> 20-30 minutes</li>
                <li>• <strong className="text-foreground">Pool + spa:</strong> 30-40 minutes</li>
                <li>• <strong className="text-foreground">Large/complex property:</strong> 40-60 minutes</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                These are guidelines — some pools need more time (heavy debris, issues to address). 
                Quality comes first, but efficient service keeps the route on schedule.
              </p>
            </div>
          </div>

          {/* Related */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Related Topics</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/wiki/service/standards" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Service Standards</span>
                <p className="text-sm text-muted-foreground mt-1">Full requirements and expectations</p>
              </Link>
              <Link to="/wiki/procedures/equipment-sops" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Equipment SOPs</span>
                <p className="text-sm text-muted-foreground mt-1">Detailed equipment procedures</p>
              </Link>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default ServiceWorkflow;